/*
 * NavView — dumb view component for the unified nav.
 *
 * COPY CONTRACT: JS mirror of
 * `dex-monorepo/ui/apps/portal/src/app/components/NavView.tsx`. Keep in sync.
 *
 * Takes a pre-filtered `entries` tree + `activePathname` + optional `header` /
 * `footer` / `iconMap` / `onItemClick`. Renders clickable items as <a href>,
 * expandable groups as a clickable header + collapsible child list. Knows
 * nothing about claims, dex, features, profile, hooks.
 *
 * Item links are real <a href> (cross-app nav = full-page reload, and they stay
 * keyboard/middle-click friendly). The wrapper may pass `onItemClick(e, path)`
 * to intercept same-surface clicks for client-side routing (no reload); it
 * should leave cross-surface clicks alone so the href reload happens.
 *
 * Styling: reuses the legacy `.navigation-item*` SCSS classes (globally loaded
 * and themed per-dex) so the shell nav matches the legacy sidebar. The root
 * <nav> carries `nav-view`, which gates the shell-only additions in
 * _navigation.scss (caret position/rotation + group-child indent). Only the
 * container layout stays inline; item/group visuals come from the classes.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';

export const NAV_VIEW_WIDTH = 240;

function isPathActive(itemPath, pathname) {
	return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

function classes(...names) {
	return names.filter(Boolean).join(' ');
}

function renderIcon(iconKey, iconMap) {
	if (!iconKey || !iconMap) return null;
	const Icon = iconMap[iconKey];
	if (!Icon) return null;
	return <Icon />;
}

function NavItemRow({ item, activePathname, iconMap, depth, onItemClick }) {
	const active = isPathActive(item.path, activePathname);
	return (
		<a
			href={item.path}
			onClick={onItemClick ? (e) => onItemClick(e, item.path) : undefined}
			className={classes(
				'navigation-item',
				active && 'navigation-item--active',
				depth > 0 && 'navigation-item--child',
			)}
		>
			<div className="navigation-item__side__rectangle" />
			<div className="navigation-item__icon">{renderIcon(item.icon, iconMap)}</div>
			<div className="navigation-item__label">{item.label}</div>
		</a>
	);
}

NavItemRow.propTypes = {
	item: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		path: PropTypes.string.isRequired,
		icon: PropTypes.string,
	}).isRequired,
	activePathname: PropTypes.string.isRequired,
	iconMap: PropTypes.object,
	depth: PropTypes.number.isRequired,
	onItemClick: PropTypes.func,
};

function NavGroupRow({ group, activePathname, iconMap, onItemClick }) {
	const hasActiveChild = group.children.some((c) => isPathActive(c.path, activePathname));
	const [open, setOpen] = useState(hasActiveChild);

	const toggle = () => setOpen(!open);

	return (
		<div>
			<div
				className={classes('navigation-item', hasActiveChild && 'navigation-item--active')}
				role="button"
				tabIndex={0}
				onClick={toggle}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						toggle();
					}
				}}
			>
				<div className="navigation-item__side__rectangle" />
				<div className="navigation-item__icon">{renderIcon(group.icon, iconMap)}</div>
				<div className="navigation-item__label">{group.label}</div>
				<div
					className={classes(
						'navigation-item__toggle',
						open && 'navigation-item__chevron--rotate',
					)}
				>
					{renderIcon('caret-down', iconMap)}
				</div>
			</div>
			{open && (
				<div>
					{group.children.map((child) => (
						<NavItemRow
							key={child.key}
							item={child}
							activePathname={activePathname}
							iconMap={iconMap}
							depth={1}
							onItemClick={onItemClick}
						/>
					))}
				</div>
			)}
		</div>
	);
}

NavGroupRow.propTypes = {
	group: PropTypes.shape({
		key: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		icon: PropTypes.string,
		children: PropTypes.array.isRequired,
	}).isRequired,
	activePathname: PropTypes.string.isRequired,
	iconMap: PropTypes.object,
	onItemClick: PropTypes.func,
};

function NavView({ entries, activePathname, header, footer, iconMap, onItemClick }) {
	return (
		<nav
			className="nav-view"
			style={{
				width: '100%',
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
				padding: '0.75rem 0',
				background: '#fafafa',
				overflowY: 'auto',
			}}
		>
			{header && <div style={{ padding: '0.5rem 1rem 0.75rem' }}>{header}</div>}

			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{entries.map((entry) =>
					entry.type === 'item' ? (
						<NavItemRow
							key={entry.key}
							item={entry}
							activePathname={activePathname}
							iconMap={iconMap}
							depth={0}
							onItemClick={onItemClick}
						/>
					) : (
						<NavGroupRow
							key={entry.key}
							group={entry}
							activePathname={activePathname}
							iconMap={iconMap}
							onItemClick={onItemClick}
						/>
					),
				)}
			</div>

			<span style={{ flex: 1 }} />

			{footer && (
				<div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #ddd' }}>{footer}</div>
			)}
		</nav>
	);
}

NavView.propTypes = {
	entries: PropTypes.array.isRequired,
	activePathname: PropTypes.string.isRequired,
	header: PropTypes.node,
	footer: PropTypes.node,
	iconMap: PropTypes.object,
	onItemClick: PropTypes.func,
};

export default NavView;
